// DailyHub — the returning-player controller for the daily loop. The home view
// is the modern Business HQ; the other views are the (transitional, dawn-styled)
// story/scene flows:
//
//   HQ (hub) → Morning Ritual → Play (run the business) → Reflect → HQ
//                      └────────────→ Raise money (Dream Bigger) ──┘
//
// "Morning done today" is derived from the persisted lastWakeUpDayKey, so the
// gate survives a reload.

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { dayKeyFromDate, visionBracket, deriveLife } from '@biz-kid/engine'; // visionBracket still used by End-of-Year
import { useGameState, selectLastWakeUpDayKey } from '@biz-kid/store';
import { MorningRitual } from './MorningRitual.js';
import { BusinessScreen } from './BusinessScreen.js';
import { Reflect } from './Reflect.js';
import { RaiseMoney } from './RaiseMoney.js';
// (the dawn World/StatusStrip behind the raise scene were removed in the re-skin)
import { ServicesScreen } from './ServicesScreen.js';
import { SocialScreen } from './SocialScreen.js';
import { GamingScreen } from './GamingScreen.js';
import { TradeScreen } from './TradeScreen.js';
import { PropertyScreen } from './PropertyScreen.js';
import { BrokerScreen } from './BrokerScreen.js';
import { HQ } from './HQ.js';
import { LifeScreen } from '../../life/LifeScreen.js';
import { ProfitHarvest } from '../../life/ProfitHarvest.js';
import { MoneyMakerUnlock } from '../../life/MoneyMakerUnlock.js';
import { VisionBoardScreen } from '../../life/VisionBoardScreen.js';
import { VisionWatcher } from '../../life/VisionWatcher.js';
import { MastermindMeeting } from '../../life/MastermindMeeting.js';
import { ConsequenceWatcher } from './ConsequenceWatcher.js';
import { ResilienceWatcher } from '../../life/ResilienceWatcher.js';
import { KnowledgeCheckDetector } from '../../life/KnowledgeCheck.js';
import { WeeklyDecisionCard } from './WeeklyDecisionCard.js';
import { MorningGate } from './MorningGate.js';
import { VoiceCoach } from '../../voice/voiceGuide.js';
import { useModalSlot } from '../../ui/modalQueue.js';
import { AnnualReview, type YearReview } from '../../life/AnnualReview.js';
import { BracketMilestone } from '../../life/BracketMilestone.js';
import { LegacyScreen } from '../../life/LegacyScreen.js';
import { useLife, useLifeStore, useLifeDispatch } from '../../life/store.js';

type View = 'hub' | 'ritual' | 'play' | 'reflect' | 'raise' | 'life' | 'vision' | 'mastermind' | 'services' | 'social' | 'gaming' | 'trade' | 'property' | 'broker';

export function DailyHub() {
  const [view, setView] = useState<View>('hub');
  // The morning breath gate is now OPT-IN — a quick buff the kid can grab, not a
  // wall that blocks the world each day.
  const [showMorningGate, setShowMorningGate] = useState(false);

  const lastWake = useGameState(selectLastWakeUpDayKey);
  const ownsVendingMachine = useGameState((s) =>
    s.assets.some((a) => a.defId === 'vendingMachine'),
  );
  const hasStand = useGameState((s) => s.businesses.length > 0);

  const today = dayKeyFromDate(new Date());
  const morningDone = lastWake === today;

  // Sims-reboot rule: nothing auto-opens. Vision Board only surfaces when a
  // dream is achieved (VisionWatcher handles celebrations). Bracket crossings
  // are marked by the in-flow BracketMilestone screen, not by hijacking
  // navigation. The old auto-open on bracket cross is removed.
  const lifeAge = useLife().age;

  // ── End of Year: advance a year, settle, then run the review → milestone →
  // (vision re-opens) flow, or the Legacy finale at 18. ───────────────────────
  const lifeDispatch = useLifeDispatch();
  const [yearFlow, setYearFlow] = useState<YearFlow>(null);

  const onEndOfYear = () => {
    const s = useLifeStore.getState().state;
    if (s.age >= 18) {
      setYearFlow({ stage: 'legacy' });
      return;
    }
    const c = deriveLife(s);
    const base = s.yearStart ?? { wealth: 0, freedomPct: 0, totalAssetValue: 0 };
    const crossed = visionBracket(s.age + 1) > visionBracket(s.age);
    lifeDispatch({ type: 'END_OF_YEAR' });
    const afterS = useLifeStore.getState().state;
    const afterC = deriveLife(afterS);
    setYearFlow({
      stage: 'review',
      review: {
        yearFrom: s.age,
        yearTo: afterS.age,
        incomeEarned: Math.round(12 * c.passiveIncome),
        invested: afterC.totalAssetValue,
        wealthChange: afterC.totalWealth - base.wealth,
        freedomMove: afterC.freedomPct - base.freedomPct,
      },
      newBracket: crossed ? visionBracket(afterS.age) : null,
      reachedCap: afterS.age >= 18,
    });
  };

  return (
    <>
      {/* Miko explains each screen aloud the FIRST time the player opens it. */}
      <VoiceCoach view={view} />

      {view === 'ritual' && <MorningRitual onComplete={() => setView('play')} />}

      {view === 'play' && (
        <BusinessScreen onReflect={() => setView('reflect')} onClose={() => setView('hub')} />
      )}

      {view === 'reflect' && <ReflectOverlay onDone={() => setView('hub')} />}

      {view === 'raise' && <RaiseMoney onComplete={() => setView('hub')} />}

      {view === 'life' && (
        <LifeScreen
          onClose={() => setView('hub')}
          onVision={() => setView('vision')}
          onMastermind={() => setView('mastermind')}
        />
      )}

      {view === 'vision' && <VisionBoardScreen onClose={() => setView('hub')} />}

      {view === 'mastermind' && <MastermindMeeting onClose={() => setView('hub')} />}

      {view === 'services' && <ServicesScreen onClose={() => setView('hub')} />}

      {view === 'social' && <SocialScreen onClose={() => setView('hub')} />}

      {view === 'gaming' && <GamingScreen onClose={() => setView('hub')} />}

      {view === 'trade' && <TradeScreen onClose={() => setView('hub')} />}

      {view === 'property' && <PropertyScreen onClose={() => setView('hub')} />}

      {view === 'broker' && <BrokerScreen onClose={() => setView('hub')} />}

      {view === 'hub' && (
        <>
          <HQ
            morningDone={morningDone}
            showRaise={!ownsVendingMachine}
            onStartMorning={() => setShowMorningGate(true)}
            onOpenStand={() => setView('play')}
            onRaise={() => setView('raise')}
            onFreedom={() => setView('life')}
            onVision={() => setView('vision')}
            onMastermind={() => setView('mastermind')}
            onServices={() => setView('services')}
            onSocial={() => setView('social')}
            onGaming={() => setView('gaming')}
            onTrade={() => setView('trade')}
            onProperty={() => setView('property')}
            onBroker={() => setView('broker')}
            onEndOfYear={onEndOfYear}
          />
          {/* Opt-in morning boost — the kid taps "Morning boost" on HQ to get
              the breath + intention buff; it never blocks the world on its own. */}
          {showMorningGate && !morningDone && <MorningGate onDone={() => setShowMorningGate(false)} />}
        </>
      )}

      {/* A decision's delayed consequence has landed — show the recap + lesson. */}
      <ConsequenceWatcher />
      {/* Reframes setbacks (loss weeks / freedom drops) as lessons; rewards comebacks. */}
      <ResilienceWatcher />
      {/* Bridges the weekly sim into the lifecycle: surfaces after each settle. */}
      <ProfitHarvest />
      {/* Full-screen "New money-maker unlocked!" flash when the first passive
          asset is acquired, with the next goal shown immediately after. */}
      <MoneyMakerUnlock />
      {/* Celebrates any dream that comes true, anywhere in the app. */}
      <VisionWatcher />
      {/* Knowledge Check — silent detector. Sets trigger flags as state
          changes. The modal opens via cottage tap in HQ, not on a timer. */}
      <KnowledgeCheckDetector />
      {/* Weekly decision card — queued modal, only mounts between weeks. */}
      <WeeklyDecisionCard />

      {/* End-of-Year flow — gated through the modal queue at top priority. */}
      <YearFlowSlot yearFlow={yearFlow} setYearFlow={setYearFlow} />
    </>
  );
}

// ---------------------------------------------------------------------------

type YearFlow =
  | null
  | { stage: 'review'; review: YearReview; newBracket: number | null; reachedCap: boolean }
  | { stage: 'milestone'; newAge: number }
  | { stage: 'legacy' };

/** Year-of-life flow gated through the modal queue at priority 95 — outranks
 *  everything else so the player-triggered Annual Review never collides with
 *  a settle-triggered Payday. */
function YearFlowSlot({ yearFlow, setYearFlow }: { yearFlow: YearFlow; setYearFlow: (y: YearFlow) => void }) {
  const { canShow } = useModalSlot('year-flow', !!yearFlow, 95);
  if (!yearFlow || !canShow) return null;
  if (yearFlow.stage === 'review') {
    return (
      <AnnualReview
        review={yearFlow.review}
        onContinue={() => {
          if (yearFlow.reachedCap) setYearFlow({ stage: 'legacy' });
          else if (yearFlow.newBracket) setYearFlow({ stage: 'milestone', newAge: yearFlow.review.yearTo });
          else setYearFlow(null);
        }}
      />
    );
  }
  if (yearFlow.stage === 'milestone') {
    return <BracketMilestone newAge={yearFlow.newAge} onContinue={() => setYearFlow(null)} />;
  }
  return <LegacyScreen onClose={() => setYearFlow(null)} />;
}

// ---------------------------------------------------------------------------

/** Reflect wrapped in a centered overlay. */
function ReflectOverlay({ onDone }: { onDone: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bk-app"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 6,
        display: 'grid',
        placeItems: 'center',
        padding: 'clamp(16px, 4vw, 40px)',
        background: 'color-mix(in srgb, var(--bg) 70%, transparent)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 520 }}>
        <Reflect onDone={onDone} />
      </div>
    </motion.div>
  );
}

